// lib/vues-registre.js — CE QUE L'ÉCRAN PEUT MONTRER, ET RIEN D'AUTRE.
//
// ══ L'EXIGENCE (Dimitri, 2026-09-08) ════════════════════════════════════════
//
//   « Je veux que l'écran de l'opérateur s'adapte au sujet dont on parle. Je
//     lui parle d'un logement, sa fiche apparaît. Je passe aux prix, l'écran
//     change. L'écran suit la conversation, il ne l'accompagne pas avec un
//     composant fixe. »
//
//   Et sa réserve, qui gouverne tout ce fichier :
//
//   « Une illustration qui n'apprend rien est pire que du texte. Pas de
//     décoration, pas d'animation pour meubler. Ça montre quelque chose ou ça
//     n'existe pas. »
//
// ══ LE MODÈLE NE DESSINE JAMAIS ═════════════════════════════════════════════
//
// Il rend un NOM DE VUE et des identifiants — `{vue:"occupation",
// logement:"lc2", periode:"12m"}`. C'est le code de Dimitri qui dessine, avec
// sa charte, sa typographie, son lotus.
//
// ⚠️ CE N'EST PAS UN CHOIX ESTHÉTIQUE, C'EST LA SEULE VERSION SÛRE. L'opérateur
// lit la boîte mail. Si un courriel pouvait lui faire produire une vue portant
// du texte arbitraire, ce serait un canal d'injection dans l'écran de Dimitri.
// Une liste FERMÉE de vues nommées ferme cela par construction — même doctrine
// que les tuples du verdict et que l'allow-list des leviers.
//
// ══ DOUZE NOMS, QUATRE DESSINS ══════════════════════════════════════════════
//
// Les vues ne sont pas douze composants : ce sont quatre FORMES réutilisées
// avec des données différentes. Ajouter une treizième vue coûte une ligne, pas
// un composant.
//
// ══ CHAQUE VUE DIT SON NOM À L'ÉCRAN ════════════════════════════════════════
//
// `titre` n'est pas décoratif. Une vue mal CHOISIE est pire qu'une absence :
// Dimitri demande ses prix, on lui dessine une occupation, et il conclut que
// l'opérateur n'a pas compris. En affichant « Occupation — douze derniers
// mois », il voit ce qui a été compris, pas seulement ce qui a été dessiné.
// Même règle que le motif figé des gestes : on doit pouvoir vérifier ce qu'on
// regarde.

// ══ LA CORRECTION QUI A REFAIT CE FICHIER (Dimitri, 2026-09-08) ═════════════
//
//   « Je ne veux pas que tes douze vues reproduisent mes onglets en miniature.
//     Si je lui demande comment vont mes réservations et qu'il me dessine une
//     version réduite de mon écran Argent, ça ne sert à rien — j'ai déjà
//     l'écran Argent, il est meilleur, et je clique dessus.
//     Le visuel doit apporter une réponse, pas une navigation. »
//
// Le premier registre comptait douze vues. HUIT ÉTAIENT DE LA NAVIGATION
// DÉGUISÉE : fiche du logement, facturation du mois, le séjour, le fil, les
// ménages, les incidents, le calendrier — chacune était un écran existant, en
// plus petit. Et « ce qui manque » était une LISTE, donc du texte.
//
// ══ LE TEST QUI REMPLACE LE PRÉCÉDENT ═══════════════════════════════════════
//
// Une vue n'est pas une section de l'application. C'est une FORME DE RÉPONSE.
// Elle doit passer deux questions, pas une :
//   1. qu'apprend-elle qu'une phrase ne dirait pas ?   (la réserve d'origine)
//   2. EXISTE-T-ELLE DÉJÀ COMME ÉCRAN ?                (le filtre du 2026-09-08)
// Si la seconde réponse est oui, la vue meurt : Dimitri a déjà l'écran, il est
// meilleur, et il clique dessus.
//
// ══ CE QUE LE FILTRE A FAIT APPARAÎTRE ══════════════════════════════════════
//
// « Pourquoi j'ai moins gagné en août ? » n'avait aucune vue — ni ici, ni dans
// l'application. C'est une forme de réponse qui n'existe nulle part : CE QUI A
// CHANGÉ ENTRE DEUX PÉRIODES, décomposé par cause. « −840 € : deux nuits vides
// de plus, une commission en hausse, un ménage refacturé. »
// C'est exactement ce qu'on demande à un bras droit, et ça n'est pas un écran.

// Les formes. Ce sont des formes de RÉPONSE, pas des types de graphique.
const FORMES = Object.freeze({
  evolution: "une grandeur dans le temps, avec le moment où elle change",
  ecart: "la différence entre deux périodes, décomposée par cause",
  classement: "des choses ordonnées, avec l'écart nommé",
  superposition: "deux séries comparées l'une à l'autre",
  repartition: "de quoi un total est fait",
  jours: "une grille de jours, quand la question porte sur des jours",
  // Les deux formes de CE QUI EST À LUI (Dimitri, 2026-09-13, en retirant la
  // pilule « L'opérateur » du bandeau) : elles ne montrent pas le parc, elles
  // montrent l'opérateur lui-même — et le seul moment où on veut les voir est
  // celui où on les demande.
  journal: "ce qu'il a fait, ligne par ligne, chacune menant à son écran",
  consignes: "ce qu'il a retenu, avec la date et la vie de chaque consigne",
});

/**
 * Les vues. Liste FERMÉE, et chacune est une forme de réponse.
 *
 *   forme        — laquelle des six
 *   titre        — ce que l'écran affiche, pour que Dimitri VOIE ce qui a été compris
 *   args         — liste fermée des paramètres (jamais un texte libre)
 *   source       — d'où viennent les chiffres (le module qui les produit)
 *   branchee     — ⚠️ CE CHAMP EST LA VÉRITÉ, PAS `source`. `source` dit d'où
 *                  les chiffres DEVRAIENT venir ; `branchee` dit si quelqu'un
 *                  va effectivement les chercher (src/lib/vues-donnees.js).
 *                  Une vue déclarée mais non branchée ne doit PAS être offerte
 *                  au modèle : il la promettrait, et l'écran rendrait un vide.
 *                  Mesuré en prod le 2026-09-08 — il a promis « je te décompose
 *                  l'écart juillet → août cause par cause » alors qu'aucun
 *                  chargeur n'existait.
 *   apprend      — ce qu'elle montre QU'UNE PHRASE NE DIRAIT PAS
 *   pas_un_ecran — POURQUOI ce n'est pas un écran existant en plus petit.
 *                  ⚠️ Champ OBLIGATOIRE : c'est le filtre du 2026-09-08, écrit.
 *                  Si on ne sait pas le remplir, la vue est de la navigation.
 */
const VUES = Object.freeze({
  evolution_point_de_rupture: {
    forme: "evolution",
    titre: "Évolution",
    args: ["mesure", "logement", "periode"],
    source: "aggregateStats",
    branchee: false,
    apprend: "le moment PRÉCIS où une grandeur décroche, marqué sur la courbe",
    pas_un_ecran:
      "l'écran Argent montre des totaux par mois ; il ne dit pas QUAND ça a basculé, ni ne l'annote",
  },
  ecart_entre_periodes: {
    forme: "ecart",
    titre: "Ce qui a changé",
    args: ["mesure", "logement", "avant", "apres"],
    source: "aggregateStats",
    branchee: false,
    apprend:
      "la décomposition d'une différence par cause — nuits vides, commissions, charges",
    pas_un_ecran:
      "AUCUN écran ne compare deux périodes : il faut ouvrir deux mois et soustraire de tête",
  },
  classement_avec_ecart: {
    forme: "classement",
    titre: "Classement",
    args: ["mesure", "periode"],
    source: "aggregateStats",
    branchee: false,
    apprend:
      "lequel décroche, et DE COMBIEN — l'écart est nommé, pas à mesurer à l'œil",
    pas_un_ecran:
      "l'écran Argent liste les logements sans les ordonner par performance ni chiffrer l'écart",
  },
  deux_series: {
    forme: "superposition",
    titre: "Comparaison",
    args: ["mesure", "logement", "serie_a", "serie_b"],
    source: "aggregateStats",
    branchee: false,
    apprend:
      "où deux séries se séparent — cette année contre l'an dernier, le prix contre le recommandé",
    pas_un_ecran:
      "rien ne superpose deux périodes ni deux sources de prix dans l'application",
  },
  repartition_total: {
    forme: "repartition",
    titre: "De quoi c'est fait",
    args: ["mesure", "logement", "periode"],
    source: "projectDataForFacturation",
    branchee: false,
    apprend:
      "la part de chaque poste dans un total, en cascade — pas en tableau",
    pas_un_ecran:
      "la facturation montre les lignes ; elle ne montre pas le POIDS relatif de chacune",
  },
  jours_a_venir: {
    forme: "jours",
    titre: "Les jours qui viennent",
    args: ["debut", "logement"],
    source: "construireAttend",
    branchee: true,
    apprend: "arrivées, départs et ménages sur une même grille de jours",
    // ⚠️ LA PLUS FRAGILE DES SIX. Elle survit parce que Dimitri l'a demandée
    // nommément et qu'une grille de jours ne se dit pas en phrases. Si elle
    // finit par ressembler au planning, elle meurt comme les huit autres.
    pas_un_ecran:
      "le planning est un mois ; celle-ci tient une semaine entière dans un seul regard, arrivées et ménages mêlés",
  },

  // ── CE QUI EST À LUI ─────────────────────────────────────────────────────
  // Dimitri, 2026-09-13 : « Son journal et sa mémoire SE DEMANDENT
  // maintenant. » Ce n'est pas une donnée du parc, c'est SA matière — et
  // l'écran qui la portait (la pilule « L'opérateur » du bandeau) a été retiré.
  // Les chargeurs (src/lib/vues-donnees.js) appellent le serveur
  // (`operateur_sien_journal` / `operateur_sien_memoire`, lib/operateur-sien.js).
  son_journal: {
    forme: "journal",
    titre: "Ce que j'ai fait",
    args: ["depuis"],
    source: "lireSonJournal",
    branchee: true,
    apprend:
      "ce que J'AI fait depuis hier soir — courriels classés, réponses préparées, prix poussés, fils passés — chaque ligne menant à son écran (« montre-moi ce que tu as fait »)",
    pas_un_ecran:
      "c'est SA matière à lui, pas une donnée du parc ; aucun écran ne la porte plus, et le seul moment où on veut la voir est celui où on la demande",
  },
  sa_memoire: {
    forme: "consignes",
    titre: "Ce que j'ai retenu",
    args: [],
    source: "saMemoire",
    branchee: true,
    apprend:
      "les consignes que J'AI retenues de toi, avec leur date, le nombre de fois où elles ont servi, et une croix pour me les faire oublier (« qu'est-ce que tu as retenu »)",
    pas_un_ecran:
      "c'est SA mémoire, pas une donnée du parc ; aucun écran ne la porte plus, et le seul moment où on veut la voir est celui où on la demande",
  },
});

/** @returns {object|null} la description d'une vue, ou null si elle n'existe pas. */
function lireVue(nom) {
  if (typeof nom !== "string") return null;
  return Object.prototype.hasOwnProperty.call(VUES, nom) ? VUES[nom] : null;
}

/**
 * Les vues RENDABLES aujourd'hui — celles qu'un chargeur sait réellement
 * nourrir. C'est cette liste, et elle seule, qui est offerte au modèle.
 *
 * ⚠️ NE PAS revenir à un filtre sur `source`. Les six vues déclarent une
 * source ; une seule est branchée. Filtrer sur `source` remettrait les cinq
 * autres dans le catalogue, le modèle les promettrait, et l'écran rendrait
 * un vide poli. Un catalogue qui annonce ce qu'on ne sait pas faire est pire
 * qu'un catalogue court.
 */
function vuesRendables() {
  return Object.keys(VUES).filter((n) => VUES[n].branchee === true);
}

/**
 * Filtre les arguments d'une vue selon sa liste fermée.
 *
 * ⚠️ Un argument hors liste REFUSE la vue entière, il n'est pas ignoré : c'est
 * par là qu'un texte arbitraire entrerait dans l'écran.
 *
 * @returns {object|null} les args retenus, ou null si l'un est interdit
 */
function filtrerArgsVue(nom, args) {
  const v = lireVue(nom);
  if (!v) return null;
  if (args == null) return {};
  if (typeof args !== "object" || Array.isArray(args)) return null;
  const permis = new Set(v.args);
  const out = {};
  for (const cle of Object.keys(args)) {
    if (!permis.has(cle)) return null;
    const val = args[cle];
    // Les valeurs sont des IDENTIFIANTS et des périodes, jamais de la prose.
    // Une borne de longueur ferme la porte à un texte glissé dans un argument.
    if (val != null && String(val).length > 40) return null;
    out[cle] = val;
  }
  return out;
}

module.exports = { VUES, FORMES, lireVue, vuesRendables, filtrerArgsVue };
